// Optimistic local-first sync registry for the ICD-10 code list
// (contextVariables["local:billableCodesICD10"]).
//
// WHY THIS EXISTS
// The ICD-10 delete/reorder path used to send its change to the server and wait
// for the round-trip + refreshChat() to repaint the list. On a slow link that
// window is large: the row stayed on screen, users re-clicked, every click sent
// a full-array replacement computed from a stale snapshot, and last-write-wins
// produced "the wrong code got deleted / it came back / it duplicated". A
// refetch landing mid-edit also wiped local changes.
//
// The fix is to let the front end own the list immediately (optimistic) and sync
// in the background. This module is the durable bookkeeping for that:
//   - it remembers the pending (unacked) ICD-10 edit per chat, mirrored to
//     localStorage so a browser close / crash / navigation does not lose it, and
//   - it reconciles an incoming server value against the pending edit so a
//     harmless re-read never clobbers the local change, while a genuine new
//     server value (e.g. an AI regeneration) is still accepted.
//
// Reconcile is by CONTENT, not timestamps. chatLastUpdate bumps for ANY field,
// so a mic-generation on another section would look "newer" for ICD-10 and
// wrongly win. Comparing the actual code list against the pre-edit baseline and
// the local value tells us unambiguously whether the server caught up, sent a
// stale read, or genuinely changed.
//
// The server merges contextVariables per key, so backgrounding this one field's
// write never clobbers other sections server-side; the only data loss this
// guards against is client-side.

import { normalizList } from "./utils";

const ICD_VARIABLE = "billableCodesICD10";
export const ICD_CONTEXT_KEY = "local:" + ICD_VARIABLE;
const STORAGE_PREFIX = "icd10:pending:";
// Bound the remembered "values we sent" list so rapid edits can't grow it
// without limit. 12 is far more than any realistic in-flight burst.
const SENT_CAP = 12;
// A pending write that never acks (chat deleted, permanent 403) would otherwise
// replay every session forever and keep diagnosis codes in localStorage. Purge
// anything older than this on load.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// chatId -> { baseline, payload, sent, updatedAt, userId }
//   baseline: the last server-synced ICD-10 value, captured BEFORE the first
//             unacked local edit (raw, may be array or keyed-object form).
//   payload:  the full contextVariables object that was sent to the server
//             (includes "local:billableCodesICD10" plus any POC/problemList
//             re-alignment keys), kept verbatim so a replay re-sends exactly
//             what the original write did.
//   sent:     signatures of every value we have sent for this chat (baseline +
//             each in-flight write). The backend emits an encounterUpdated SSE
//             after EVERY write, so a refetch can surface an EARLIER in-flight
//             value before a later one acks. Without this set the reconcile
//             would misread that echo as a foreign change, flicker a code back,
//             and fire a spurious "codes were updated" toast.
//   userId:   who made the edit. Replay must never fire a previous user's
//             pending write under whoever logs in next on a shared workstation.
const memory = new Map();

// chatId -> count of OUR ICD-10 writes currently on the wire. While any write
// is in flight, an ack for an earlier write must not clear the pending record
// (a later in-flight write still needs its protection), and a refetch carrying
// an unrecognized value is far more likely an interleaved stale read than a
// genuine foreign change — accepting it mid-storm is exactly the
// "deleted code resurrects on slow links" bug. In-memory only: a write cannot
// survive a page unload, so neither should its in-flight count.
const inflight = new Map();

export function beginWrite(chatId) {
  if (!chatId) return;
  inflight.set(chatId, (inflight.get(chatId) || 0) + 1);
}

export function endWrite(chatId) {
  if (!chatId) return;
  const n = (inflight.get(chatId) || 0) - 1;
  if (n > 0) inflight.set(chatId, n);
  else inflight.delete(chatId);
}

function inflightCount(chatId) {
  return inflight.get(chatId) || 0;
}

// chatId -> tail of the per-chat write chain. Every ICD-10-bearing write MUST
// go through enqueueWrite so writes leave the client strictly in user-intent
// order. Without this, a slow earlier write (ordering seed, POC/problem-list
// alignment) can land on the server AFTER a later delete and win the
// last-write-wins merge — the deleted code resurrects even though every write
// was individually "protected". Serializing trades a little write latency
// (invisible: the UI is optimistic) for a server history that always ends on
// the user's latest intent.
const chains = new Map();

export function enqueueWrite(chatId, fn) {
  const prev = chains.get(chatId) || Promise.resolve();
  // The chain must survive a failed link (the failure is handled by the
  // caller's own .catch + the replay effect), so swallow here.
  const next = prev.then(
    () => fn(),
    () => fn()
  );
  chains.set(
    chatId,
    next.catch(() => {})
  );
  return next;
}

// A normalized, order-sensitive signature of an ICD-10 value. Order matters
// because a reorder is itself a real change. normalizList accepts the array
// form, the keyed-object form and a JSON string holding either, so this is
// shape-agnostic: a list stored as text signs the same as its parsed array,
// which is what the reconcile should see.
function signature(value) {
  // Belt and braces. normalizList no longer throws on malformed shapes (null
  // entries and null dict values are dropped since #2774), but signature()
  // runs inside the delete/reorder click handlers and the refreshChat
  // reconcile, so an unforeseen shape must still degrade to "unknown" rather
  // than break the edit or abort the whole chat refresh.
  try {
    return normalizList(value)
      .map((e) => `${e?.code ?? ""}|${e?.modifier ?? ""}|${e?.description ?? ""}`)
      .join("¦");
  } catch (e) {
    return "__icd10_sig_error__";
  }
}

export function sameIcd(a, b) {
  return signature(a) === signature(b);
}

function storageKey(chatId) {
  return STORAGE_PREFIX + chatId;
}

function persist(chatId, record) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(storageKey(chatId), JSON.stringify(record));
  } catch (e) {
    // Private mode / quota — in-memory still works for this session.
  }
}

function unpersist(chatId) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.removeItem(storageKey(chatId));
  } catch (e) {
    /* ignore */
  }
}

function now() {
  // Date.now is intentionally used (not injected) — this is runtime bookkeeping,
  // not a pure function under test for its timestamp.
  return new Date().getTime();
}

// Register (or update) the pending ICD-10 edit for a chat. The baseline is
// captured once and preserved across successive edits before an ack, so the
// reconcile can always tell a stale re-read (== baseline) from a real change.
export function setPending(chatId, { baseline, payload, userId }) {
  if (!chatId || !payload) return;
  const existing = memory.get(chatId);
  // Track every signature we've sent (baseline first, then each write) so a
  // refetch echoing an earlier in-flight value is recognized, not clobbered.
  const sent = existing && Array.isArray(existing.sent) ? existing.sent.slice() : [signature(baseline)];
  const sig = signature(payload[ICD_CONTEXT_KEY]);
  if (!sent.includes(sig)) sent.push(sig);
  const record = {
    baseline: existing ? existing.baseline : baseline,
    payload,
    sent: sent.slice(-SENT_CAP),
    updatedAt: now(),
    userId: userId ?? existing?.userId ?? null,
  };
  memory.set(chatId, record);
  persist(chatId, record);
}

export function getPending(chatId) {
  if (!chatId) return null;
  if (memory.has(chatId)) return memory.get(chatId);
  // Hydrate lazily from storage (e.g. after a reload before loadAll ran).
  // Mirror loadAll's shape + TTL validation: caching a payload-less or expired
  // record here would make every consumer (pendingValue, reconcileIncoming)
  // throw on p.payload — and reconcile runs inside refreshChat, so one corrupt
  // entry would silently abort every refresh of that chat until cleared.
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(storageKey(chatId));
    if (!raw) return null;
    const record = JSON.parse(raw);
    if (!record || !record.payload || (record.updatedAt && now() - record.updatedAt > MAX_AGE_MS)) {
      unpersist(chatId);
      return null;
    }
    memory.set(chatId, record);
    return record;
  } catch (e) {
    unpersist(chatId);
    return null;
  }
}

export function hasPending(chatId) {
  return !!getPending(chatId);
}

export function pendingValue(chatId) {
  const p = getPending(chatId);
  return p ? p.payload[ICD_CONTEXT_KEY] : undefined;
}

export function clear(chatId) {
  memory.delete(chatId);
  unpersist(chatId);
}

// Called when a write for `syncedValue` was acknowledged by the server. Clear
// the pending record only if the current local value still matches what we just
// synced; if a newer edit superseded it, leave the record (its own write acks).
// Also refuse to clear while OTHER writes are still in flight (count > 1: the
// ack'ing write is still counted until its endWrite runs) — an earlier slow
// write landing after this ack would otherwise hit the server unprotected and
// resurrect the change this ack just confirmed.
export function onAck(chatId, syncedValue) {
  const p = getPending(chatId);
  if (!p) return;
  if (inflightCount(chatId) > 1) return;
  if (sameIcd(p.payload[ICD_CONTEXT_KEY], syncedValue)) {
    clear(chatId);
  }
}

// Called when a write was REFUSED before it ever reached the server because
// ownership of the encounter could not be transferred to the acting user (see
// useChats ensureAssignedToMe). The record must not outlive the refusal: the
// replay effect re-fires pending payloads with skipAssign: true, so a refused
// write left in the queue lands on the teammate's chart later with the
// ownership gate bypassed entirely.
//
// Scoped to writes that actually carry the ICD-10 key. A refused write for some
// OTHER section says nothing about this record — that section's own write made
// its own decision, and dropping an unrelated pending edit on a transient
// assignment failure would lose work the user is still entitled to sync.
//
// Deliberately NOT value-matched against the record (unlike onAck): a reload
// between two refused edits would leave the newer record replayable, and every
// payload in that lineage is equally unauthorized right now. Clearing costs
// nothing in-session — the record is crash-replay bookkeeping, not the write
// itself, so a queued write whose assignment succeeds on a later retry still
// fires and still lands.
export function onRefused(chatId, refusedContextVariables) {
  if (!chatId || !refusedContextVariables) return;
  if (!Object.prototype.hasOwnProperty.call(refusedContextVariables, ICD_CONTEXT_KEY)) return;
  clear(chatId);
}

// Decide what to do with an incoming server ICD-10 value given a pending edit.
//   { action: "none" }                      no pending — caller proceeds normally
//   { action: "keepLocal", value, payload } stale re-read — keep the local value.
//       `payload` is the full pending contextVariables: when the ICD value is a
//       stale echo, the same refetch's POC/problem-list realignment keys are
//       equally stale, so the caller should restore all of them, not just ICD.
//   { action: "accept" }                    server caught up to our edit
//   { action: "accept", changed: true }     genuine new server value — accept + notify
// Side effect: clears the pending record on either "accept" outcome.
export function reconcileIncoming(chatId, incoming) {
  const p = getPending(chatId);
  if (!p) return { action: "none" };

  const local = p.payload[ICD_CONTEXT_KEY];

  if (sameIcd(incoming, local)) {
    // Server caught up — but only drop the protection once nothing of ours is
    // still on the wire. Clearing here while an earlier slow write was still
    // in flight left its late landing unguarded (it re-wrote the pre-edit
    // list server-side and the next refetch resurrected a deleted code).
    if (inflightCount(chatId) === 0) clear(chatId);
    return { action: "accept" };
  }
  // An echo of our baseline or any earlier in-flight write is a stale re-read,
  // not a foreign change — keep the local value. (sent includes the baseline
  // signature; the `|| []` guards records persisted before sent existed.)
  if ((p.sent || []).includes(signature(incoming))) {
    return { action: "keepLocal", value: local, payload: p.payload };
  }
  // Unrecognized value while our own writes are still in flight: with multiple
  // writers racing (seed, alignment, delete) plus refresh chains, a refetch can
  // surface an interleaving we never sent. Treat it as stale until our last
  // write acks — the post-ack refetch settles genuine foreign changes — rather
  // than clearing the record mid-storm and letting a slow stale write win.
  if (inflightCount(chatId) > 0) {
    return { action: "keepLocal", value: local, payload: p.payload };
  }
  clear(chatId);
  return { action: "accept", changed: true };
}

// All persisted pending writes (for crash/close replay on startup or reconnect).
export function loadAll() {
  const out = [];
  try {
    if (typeof localStorage === "undefined") return out;
    // Snapshot the keys first. The TTL purge below removes entries, and removing
    // during a live index walk (localStorage.key(i)) shifts indices and skips
    // the neighbor that slid into the vacated slot.
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) keys.push(k);
    }
    for (const key of keys) {
      const chatId = key.slice(STORAGE_PREFIX.length);
      try {
        const record = JSON.parse(localStorage.getItem(key));
        if (record && record.payload) {
          // Purge writes that never acked (chat deleted / permanent failure) so
          // they don't replay forever or keep diagnosis codes around at rest.
          if (record.updatedAt && now() - record.updatedAt > MAX_AGE_MS) {
            try {
              localStorage.removeItem(key);
            } catch (e) {
              /* ignore */
            }
            continue;
          }
          memory.set(chatId, record);
          out.push({ chatId, payload: record.payload, updatedAt: record.updatedAt, userId: record.userId ?? null });
        }
      } catch (e) {
        /* skip corrupt entry */
      }
    }
  } catch (e) {
    /* ignore */
  }
  return out;
}

// Logout hygiene: drop every pending record (memory + localStorage). The
// payloads carry clinical narrative (plan of care), so they must not survive a
// logout on a shared workstation or replay under the next user's session.
export function clearAll() {
  memory.clear();
  inflight.clear();
  try {
    if (typeof localStorage === "undefined") return;
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) keys.push(k);
    }
    for (const k of keys) localStorage.removeItem(k);
  } catch (e) {
    /* ignore */
  }
}

// Test-only: reset in-memory state (does not touch localStorage).
export function __resetMemory() {
  memory.clear();
  inflight.clear();
  chains.clear();
}
